import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Broadcaster } from '../lib/webrtc';
import { Button } from './ui/button';
import { Camera, StopCircle, Users, Heart, Mic, SwitchCamera, CheckCircle2, Radio } from 'lucide-react';
import { HeartsOverlay, useHearts } from './LiveHearts';

function useMicLevel(stream) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!stream || !stream.getAudioTracks().length) { setLevel(0); return; }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 256;
    src.connect(an);
    const buf = new Uint8Array(an.frequencyBinCount);
    let raf;
    const tick = () => {
      an.getByteFrequencyData(buf);
      setLevel(Math.min(100, Math.round((buf.reduce((a, b) => a + b, 0) / buf.length) * 1.6)));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); ctx.close().catch(() => {}); };
  }, [stream]);
  return level;
}

export default function WebRTCBroadcaster({ liveId }) {
  const videoRef = useRef(null);
  const bcRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [running, setRunning] = useState(false);
  const [facing, setFacing] = useState('user');
  const [viewers, setViewers] = useState(0);
  const [conn, setConn] = useState('idle');
  const { floating, count, pop } = useHearts();
  const mic = useMicLevel(stream);

  const openCamera = async (mode = facing) => {
    try {
      stream?.getTracks().forEach((t) => t.stop());
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: { echoCancellation: true, noiseSuppression: true } });
      setStream(s);
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
      setFacing(mode);
      return s;
    } catch (e) {
      toast.error('Impossible d\'accéder à la caméra ou au micro. Autorisez l\'accès dans votre navigateur.');
      return null;
    }
  };

  const flip = async () => {
    const next = facing === 'user' ? 'environment' : 'user';
    const s = await openCamera(next);
    if (s && bcRef.current) {
      bcRef.current.stream = s;
      bcRef.current.viewers.forEach((pc) => pc.getSenders().forEach((sn) => {
        const t = s.getTracks().find((x) => x.kind === sn.track?.kind);
        if (t) sn.replaceTrack(t);
      }));
    }
  };

  const goLive = async () => {
    const s = stream || (await openCamera());
    if (!s) return;
    try {
      const bc = new Broadcaster(liveId, s, setConn, (ev) => {
        if (ev.type === 'heart') pop(ev.n);
        if (ev.type === 'replaced') { toast.error('Diffusion reprise depuis un autre appareil ou onglet'); stop(); }
        if (ev.type === 'unauthorized') { toast.error('Session expirée : reconnectez-vous puis relancez la diffusion'); stop(); }
        if (ev.type === 'ended') { toast.error('Ce live est terminé'); stop(); }
      });
      await bc.connect();
      bcRef.current = bc;
      setRunning(true);
      toast.success('Vous êtes en direct !');
      bc._iv = setInterval(() => setViewers(bc.viewers.size), 1000);
    } catch (e) {
      toast.error(e?.message === 'no-token' ? 'Connectez-vous pour diffuser (session expirée)' : 'Connexion au serveur impossible : vérifiez votre réseau et réessayez');
    }
  };

  const stop = () => {
    if (bcRef.current) { clearInterval(bcRef.current._iv); bcRef.current.viewers.forEach((pc) => pc.close()); bcRef.current.viewers.clear(); try { bcRef.current.ws?.close(); } catch {} bcRef.current = null; }
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
    setRunning(false);
    toast('Diffusion arrêtée');
  };

  useEffect(() => () => { if (bcRef.current) { clearInterval(bcRef.current._iv); bcRef.current.stop(); } stream?.getTracks().forEach((t) => t.stop()); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasVideo = !!stream?.getVideoTracks().length;
  const hasAudio = !!stream?.getAudioTracks().length;

  return (
    <div className="space-y-3">
      <div className="relative rounded-2xl overflow-hidden border border-slate-800 aspect-video bg-black">
        <video ref={videoRef} data-testid="broadcaster-video" autoPlay muted playsInline className={`w-full h-full object-cover ${facing === 'user' ? 'scale-x-[-1]' : ''}`} />
        {!stream && (
          <div className="absolute inset-0 grid place-items-center text-slate-400 text-sm text-center px-6">
            <div><Camera className="h-8 w-8 mx-auto mb-2 text-slate-500" />Testez votre caméra et votre micro avant de passer en direct.</div>
          </div>
        )}
        {stream && !running && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-sky-600 text-white text-xs font-bold" data-testid="preview-badge">
            <Camera className="h-3 w-3" /> APERÇU — personne ne vous voit encore
          </div>
        )}
        {running && (
          <>
            <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-600 text-white text-xs font-bold glow-live" data-testid="live-badge">
              <span className="live-dot" /> EN DIRECT
            </div>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-950/70 backdrop-blur text-white text-xs" data-testid="broadcaster-conn">
              <span className={`h-2 w-2 rounded-full ${['connected', 'live'].includes(conn) ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'}`} />
              {['connected', 'live'].includes(conn) ? 'Serveur OK' : 'Reconnexion…'} · <Users className="h-3 w-3" /> {viewers}
            </div>
            <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-600/80 backdrop-blur text-white text-xs font-bold" data-testid="broadcaster-hearts">
              <Heart className="h-3 w-3" fill="white" /> {count}
            </div>
            <HeartsOverlay floating={floating} />
          </>
        )}
        {stream && (
          <div className="absolute bottom-3 left-3 flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-slate-950/70 backdrop-blur text-white text-xs" data-testid="mic-meter">
            <Mic className={`h-3.5 w-3.5 ${mic > 8 ? 'text-emerald-400' : 'text-slate-400'}`} />
            <div className="h-1.5 w-20 rounded-full bg-slate-700 overflow-hidden">
              <div className="h-full bg-emerald-400 transition-[width] duration-75" style={{ width: `${mic}%` }} />
            </div>
            <span className="text-slate-300" data-testid="mic-status">{mic > 8 ? 'Micro OK' : 'Parlez…'}</span>
          </div>
        )}
      </div>

      {stream && !running && (
        <div className="grid grid-cols-2 gap-2 text-xs" data-testid="device-check">
          <div className={`rounded-xl px-3 py-2 border ${hasVideo ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-rose-500/40 bg-rose-500/10 text-rose-300'}`} data-testid="check-camera">
            <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />{hasVideo ? 'Caméra détectée' : 'Pas de caméra'}
          </div>
          <div className={`rounded-xl px-3 py-2 border ${hasAudio ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-rose-500/40 bg-rose-500/10 text-rose-300'}`} data-testid="check-mic">
            <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />{hasAudio ? 'Micro détecté' : 'Pas de micro'}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!stream && (
          <Button data-testid="test-camera-btn" onClick={() => openCamera()} className="btn-brand text-white">
            <Camera className="h-4 w-4 mr-2" />Tester caméra & micro
          </Button>
        )}
        {stream && !running && (
          <Button data-testid="start-broadcast-btn" onClick={goLive} className="bg-red-600 hover:bg-red-500 text-white font-bold">
            <Radio className="h-4 w-4 mr-2" />Passer en direct
          </Button>
        )}
        {stream && (
          <Button data-testid="flip-camera-btn" onClick={flip} variant="outline" className="border-slate-700 text-slate-100 bg-transparent">
            <SwitchCamera className="h-4 w-4 mr-2" />Changer de caméra
          </Button>
        )}
        {stream && (
          <Button data-testid="stop-broadcast-btn" onClick={stop} variant="outline" className="border-slate-700 text-slate-100 bg-transparent">
            <StopCircle className="h-4 w-4 mr-2" />{running ? 'Arrêter la diffusion' : 'Fermer l\'aperçu'}
          </Button>
        )}
      </div>
    </div>
  );
}
